import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Plus, StickyNote } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { SearchSelect } from '@/components/composites/search-select'
import { ConfirmAction } from '@/components/composites/confirm-action'
import { cn } from '@/lib/utils'
import type { Risk } from '@/features/projects'
import { lifecycleApi, useLifecycleWrite } from './api'
import {
  PHASES,
  PHASE_LABEL,
  PRIORITIES,
  TASK_KINDS,
  TASK_KIND_LABEL,
  TASK_STATUSES,
  TASK_STATUS_LABEL,
  TEAMS,
  TEAM_LABEL,
  TEAM_LONG,
  dayOf,
  isOverdue,
  isoOfDay,
  shortDate,
  type Lifecycle,
  type Phase,
  type RegistryTask,
  type TaskStatus,
} from './model'

const ALL = 'all'
const STATUS_TONE: Record<TaskStatus, string> = {
  not_started: 'bg-neutral-soft text-muted-foreground',
  in_progress: 'bg-info-soft text-info',
  blocked: 'bg-danger-soft text-danger',
  done: 'bg-success-soft text-success',
}
const levelLabel = (l: string) => l[0]!.toUpperCase() + l.slice(1)

type People = Array<{ id: string; label: string; photoUrl?: string | null }>

/**
 * The action registry: the project's single task list. Filters live in the URL, so a filtered
 * registry pasted into Slack opens filtered. The AM edits everything; a task's assignee may move
 * its status (the server enforces both).
 */
export function ActionRegistry({
  projectId,
  lifecycle,
  tasks,
  risks,
  people,
  canManage,
  myId,
}: {
  projectId: string
  lifecycle: Lifecycle
  tasks: RegistryTask[]
  risks: Risk[]
  people: People
  canManage: boolean
  myId: string
}) {
  const [params, setParams] = useSearchParams()
  const phase = params.get('rphase') ?? ALL
  const team = params.get('rteam') ?? ALL
  const status = params.get('rstatus') ?? ALL
  const overdueOnly = params.get('roverdue') === '1'
  const [editing, setEditing] = useState<RegistryTask | 'new' | null>(null)
  const [now] = useState(() => Date.now())
  const setParam = (key: string, value: string | null) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === null || value === ALL) next.delete(key)
      else next.set(key, value)
      return next
    }, { replace: true })
  const nameOf = useMemo(() => new Map(people.map((p) => [p.id, p.label])), [people])
  const bySeq = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks])
  const riskName = useMemo(() => new Map(risks.map((r) => [r.id, r.text])), [risks])
  const setStatus = useLifecycleWrite(projectId, ({ id, value }: { id: string; value: TaskStatus }) => lifecycleApi.patchTask(projectId, id, { status: value }))

  const rows = tasks.filter(
    (t) =>
      (phase === ALL || t.phase === phase) &&
      (team === ALL || t.ownerTeam === team) &&
      (status === ALL || t.status === status) &&
      (!overdueOnly || isOverdue(t, now)),
  )
  const counts = { open: tasks.filter((t) => t.status !== 'done').length, overdue: tasks.filter((t) => isOverdue(t, now)).length }
  const phasesPresent = PHASES.filter((p) => tasks.some((t) => t.phase === p))

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Phase" value={phase} onChange={(v) => setParam('rphase', v)} options={phasesPresent.map((p) => ({ value: p, label: PHASE_LABEL[p] }))} />
        <FilterSelect label="Team" value={team} onChange={(v) => setParam('rteam', v)} options={TEAMS.map((t) => ({ value: t, label: TEAM_LONG[t] }))} />
        <FilterSelect label="Status" value={status} onChange={(v) => setParam('rstatus', v)} options={TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_LABEL[s] }))} />
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={overdueOnly} onCheckedChange={(v) => setParam('roverdue', v ? '1' : null)} />
          Overdue only
        </label>
        <span className="ml-auto text-xs text-muted-foreground">
          {counts.open} open · <span className={cn(counts.overdue > 0 && 'font-semibold text-danger')}>{counts.overdue} overdue</span>
        </span>
        {canManage ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="size-3.5" /> Add task
          </Button>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[68rem] text-sm">
          <thead className="bg-surface-sunken text-left text-tiny uppercase tracking-wide text-text-muted">
            <tr>
              {['ID', 'Task', 'Phase / cycle', 'Owner team', 'Assignee', 'Start', 'Due', 'Status', 'Priority', 'Depends on', 'Linked risk', ''].map((h) => (
                <th key={h || 'notes'} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">No tasks match these filters.</td></tr>
            ) : (
              rows.map((t) => {
                const late = isOverdue(t, now)
                const mine = t.assigneeId === myId
                const dep = t.dependsOn ? bySeq.get(t.dependsOn) : null
                return (
                  <tr key={t.id} className="border-t border-border-soft align-top hover:bg-accent/40">
                    <td className="num whitespace-nowrap px-3 py-2 text-muted-foreground">T-{t.seq}</td>
                    <td className="max-w-[22rem] px-3 py-2">
                      <button type="button" className="text-left font-medium hover:text-brand disabled:hover:text-foreground" disabled={!canManage} onClick={() => setEditing(t)}>
                        {t.title}
                      </button>
                      {t.kind !== 'task' ? <span className={cn('ml-1.5 rounded px-1.5 py-px text-[11px] font-semibold', t.kind === 'flag' || t.kind === 'bug' ? 'bg-danger-soft text-danger' : 'bg-info-soft text-info')}>{TASK_KIND_LABEL[t.kind]}</span> : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{PHASE_LABEL[t.phase]}{t.cycle > 1 && t.phase !== 'post_sales' ? ` · C${t.cycle}` : ''}</td>
                    <td className="whitespace-nowrap px-3 py-2">{TEAM_LABEL[t.ownerTeam]}</td>
                    <td className="whitespace-nowrap px-3 py-2">{t.assigneeId ? nameOf.get(t.assigneeId) ?? 'Someone' : <span className="text-text-muted">—</span>}</td>
                    <td className="num whitespace-nowrap px-3 py-2 text-muted-foreground">{shortDate(t.startAt)}</td>
                    <td className={cn('num whitespace-nowrap px-3 py-2', late && 'font-semibold text-danger')}>{shortDate(t.dueAt)}</td>
                    <td className="px-3 py-1.5">
                      {canManage || mine ? (
                        <Select value={t.status} onValueChange={(v) => setStatus.mutate({ id: t.id, value: v as TaskStatus })}>
                          <SelectTrigger className={cn('h-7 w-32 border-0 text-xs font-semibold', STATUS_TONE[t.status])} aria-label={`Status of ${t.title}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', STATUS_TONE[t.status])}>{TASK_STATUS_LABEL[t.status]}</span>
                      )}
                    </td>
                    <td className={cn('whitespace-nowrap px-3 py-2 capitalize', t.priority === 'critical' && 'font-semibold text-danger')}>{t.priority}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{dep ? `T-${dep.seq}` : '—'}</td>
                    <td className="max-w-[10rem] truncate px-3 py-2 text-muted-foreground" title={t.riskId ? riskName.get(t.riskId) : undefined}>{t.riskId ? riskName.get(t.riskId) ?? '—' : '—'}</td>
                    <td className="px-3 py-2">{t.notes ? <StickyNote className="size-3.5 text-muted-foreground" aria-label="Has notes" /> : null}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {editing ? (
        <TaskDialog
          projectId={projectId}
          lifecycle={lifecycle}
          task={editing === 'new' ? null : editing}
          tasks={tasks}
          risks={risks}
          people={people}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-44 text-xs" aria-label={label}>
        <SelectValue>{value === ALL ? `All ${label.toLowerCase()}s` : options.find((o) => o.value === value)?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label.toLowerCase()}s</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

const NONE = '__none__'

/** Add or edit one registry task. Only what changed is sent. */
function TaskDialog({ projectId, lifecycle, task, tasks, risks, people, onClose }: { projectId: string; lifecycle: Lifecycle; task: RegistryTask | null; tasks: RegistryTask[]; risks: Risk[]; people: People; onClose: () => void }) {
  const [title, setTitle] = useState(task?.title ?? '')
  const [phase, setPhase] = useState<Phase>(task?.phase ?? lifecycle.currentPhase)
  const [ownerTeam, setOwnerTeam] = useState(task?.ownerTeam ?? 'am')
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? '')
  const [start, setStart] = useState(dayOf(task?.startAt))
  const [due, setDue] = useState(dayOf(task?.dueAt))
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? 'not_started')
  const [priority, setPriority] = useState(task?.priority ?? 'medium')
  const [kind, setKind] = useState(task?.kind ?? 'task')
  const [dependsOn, setDependsOn] = useState(task?.dependsOn ?? NONE)
  const [riskId, setRiskId] = useState(task?.riskId ?? NONE)
  const [notes, setNotes] = useState(task?.notes ?? '')
  const [error, setError] = useState<string | null>(null)

  const save = useLifecycleWrite(projectId, (body: Record<string, unknown>) => (task ? lifecycleApi.patchTask(projectId, task.id, body) : lifecycleApi.addTask(projectId, body)), task ? 'Task saved' : 'Task added to the registry')
  const remove = useLifecycleWrite(projectId, () => lifecycleApi.archiveTask(projectId, task!.id), 'Task removed from the registry')

  const submit = () => {
    if (!title.trim()) return setError('Give the task a title.')
    if (start && due && due < start) return setError('A task cannot be due before it starts.')
    const full: Record<string, unknown> = {
      title: title.trim(),
      phase,
      ownerTeam,
      assigneeId: assigneeId || null,
      startAt: start ? isoOfDay(start) : null,
      dueAt: due ? isoOfDay(due) : null,
      status,
      priority,
      kind,
      dependsOn: dependsOn === NONE ? null : dependsOn,
      riskId: riskId === NONE ? null : riskId,
      notes: notes.trim() || null,
    }
    let body = full
    if (task) {
      const before: Record<string, unknown> = {
        title: task.title, phase: task.phase, ownerTeam: task.ownerTeam, assigneeId: task.assigneeId, startAt: task.startAt ? isoOfDay(dayOf(task.startAt)) : null, dueAt: task.dueAt ? isoOfDay(dayOf(task.dueAt)) : null,
        status: task.status, priority: task.priority, kind: task.kind, dependsOn: task.dependsOn, riskId: task.riskId, notes: task.notes,
      }
      body = Object.fromEntries(Object.entries(full).filter(([k, v]) => before[k] !== v))
      if (!Object.keys(body).length) return onClose()
    }
    setError(null)
    save.mutate(body, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{task ? `T-${task.seq} · Edit task` : 'Add to the action registry'}</DialogTitle>
          <DialogDescription>Tasks, support tickets after go-live, bugs and feature gaps all live here. Bugs and feature gaps go to Product & Engineering in the health score.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="task-title">Task</Label>
            <Input id="task-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Pick label="Kind" value={kind} onChange={(v) => setKind(v as typeof kind)} options={TASK_KINDS.map((k) => ({ value: k, label: TASK_KIND_LABEL[k] }))} />
            <Pick label="Phase" value={phase} onChange={(v) => setPhase(v as Phase)} options={PHASES.map((p) => ({ value: p, label: PHASE_LABEL[p] }))} />
            <Pick label="Owner team" value={ownerTeam} onChange={(v) => setOwnerTeam(v as typeof ownerTeam)} options={TEAMS.map((t) => ({ value: t, label: TEAM_LONG[t] }))} />
            <Pick label="Status" value={status} onChange={(v) => setStatus(v as TaskStatus)} options={TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_LABEL[s] }))} />
            <Pick label="Priority" value={priority} onChange={(v) => setPriority(v as typeof priority)} options={PRIORITIES.map((p) => ({ value: p, label: levelLabel(p) }))} />
            <div className="grid gap-1.5">
              <Label>Assignee</Label>
              <SearchSelect value={assigneeId} onChange={setAssigneeId} options={people} placeholder="Search people…" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="task-start">Start</Label>
              <Input id="task-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="task-due">Due</Label>
              <Input id="task-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
            <Pick label="Depends on" value={dependsOn} onChange={setDependsOn} options={[{ value: NONE, label: 'Nothing' }, ...tasks.filter((t) => t.id !== task?.id).map((t) => ({ value: t.id, label: `T-${t.seq} ${t.title}` }))]} />
          </div>
          <Pick label="Linked risk" value={riskId} onChange={setRiskId} options={[{ value: NONE, label: 'None' }, ...risks.map((r) => ({ value: r.id, label: r.text }))]} />
          <div className="grid gap-1.5">
            <Label htmlFor="task-notes">Notes</Label>
            <Textarea id="task-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {task?.templateKey ? <p className="text-tiny text-text-muted">From the lifecycle template. Its dates move with its phase until it is started.</p> : null}
          {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {task ? (
            <ConfirmAction action="Remove" subject={`T-${task.seq}`} tone="danger" description="It leaves the registry. Nothing is deleted; it is archived." onConfirm={() => remove.mutate(undefined, { onSuccess: onClose })}>
              <Button variant="ghost" className="text-danger" disabled={remove.isPending}>Remove from registry</Button>
            </ConfirmAction>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : task ? 'Save' : 'Add task'}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Pick({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  )
}
